import React, { useMemo } from 'react';
import { Box, Stack, Typography, useTheme, alpha } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';

/**
 * Event-study response curve: mean abnormal move vs τ since occurrence,
 * with the bootstrap CI band and the fitted half-life annotated
 * (spec §7 J3 — EventResponseChart).
 */
const EventResponseChart = ({ study, height = 200 }) => {
  const theme = useTheme();

  const { series, halfLife } = useMemo(() => {
    const resp = study?.response || [];
    return {
      series: [
        {
          id: 'hi',
          data: resp.map((r) => ({ x: r.tau, y: r.hi })),
        },
        {
          id: 'lo',
          data: resp.map((r) => ({ x: r.tau, y: r.lo })),
        },
        {
          id: 'mean',
          data: resp.map((r) => ({ x: r.tau, y: r.mean })),
        },
      ],
      halfLife: study?.half_life_bins,
    };
  }, [study]);

  if (!study || !(study.response || []).length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No event study available (needs at least 5 occurrences in the window).
      </Typography>
    );
  }

  const colorFor = (id) => (id === 'mean'
    ? theme.palette.warning.main
    : alpha(theme.palette.warning.main, 0.25));

  return (
    <Box>
      <Box sx={{ height }}>
        <ResponsiveLine
          data={series}
          margin={{ top: 8, right: 12, bottom: 36, left: 48 }}
          xScale={{ type: 'linear', min: 0, max: 'auto' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          curve="monotoneX"
          colors={(s) => colorFor(s.id)}
          lineWidth={2}
          enablePoints={false}
          axisBottom={{
            legend: 'bins since event (τ)',
            legendPosition: 'middle',
            legendOffset: 28,
            tickValues: 6,
            tickSize: 0,
          }}
          axisLeft={{
            legend: 'abnormal move (σ units)',
            legendPosition: 'middle',
            legendOffset: -40,
            tickValues: 4,
            tickSize: 0,
            format: '.2~f',
          }}
          markers={[
            {
              axis: 'y', value: 0,
              lineStyle: { stroke: alpha(theme.palette.text.primary, 0.25), strokeDasharray: '4 4' },
            },
            ...(halfLife ? [{
              axis: 'x', value: halfLife,
              lineStyle: { stroke: theme.palette.warning.main, strokeDasharray: '2 4' },
              legend: `half-life ≈ ${halfLife} bins`,
              legendOrientation: 'vertical',
              textStyle: { fill: theme.palette.text.secondary, fontSize: 10 },
            }] : []),
          ]}
          animate={false}
          useMesh
          theme={{
            text: { fill: theme.palette.text.secondary, fontSize: 10 },
            grid: { line: { stroke: alpha(theme.palette.text.primary, 0.06) } },
          }}
          tooltip={({ point }) => (
            <Box sx={{ p: 0.75, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3 }}>
              <Typography variant="caption">
                τ = {point.data.x}: {Number(point.data.y).toFixed(3)}σ
              </Typography>
            </Box>
          )}
        />
      </Box>
      <Stack direction="row" spacing={2} sx={{ px: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {study.n_events} occurrences
        </Typography>
        <Typography variant="caption" color="text.secondary">
          band: bootstrap 90% CI
        </Typography>
      </Stack>
    </Box>
  );
};

export default EventResponseChart;
